import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { PAGINATION_LIMIT } from '@repo/toolkit/constants/pagination'
import { DateFormatter } from '@repo/toolkit/date'
import { ArrayKeys } from '@repo/toolkit/types'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  type Row as TanstackRow,
  type SortingState,
  type Table as TanstackTable,
  useReactTable
} from '@tanstack/react-table'
import React, { Fragment, useImperativeHandle, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'

import useTranslate from '../../../../../apps/backoffice/src/lib/hooks/use-translate'
import { PlusIcon } from '../../../icons'
import ArrowDownIcon from '../../../icons/arrow-down'
import ArrowUpIcon from '../../../icons/arrow-up'
import ArrowUpDownIcon from '../../../icons/arrow-up-down'
import SolidUpArrowIcon from '../../../icons/solid-up-arrow'
import Checkbox from '../../atoms/checkbox'
import Loader from '../../atoms/loader'
import Typography from '../../atoms/typography'
import Pagination from './pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from './table'

export { type CellContext } from '@tanstack/react-table'

export type TableRequiredFields = {
  id?: number
  rowColor?: TailwindColor
  rowBorderColor?: TailwindColor
  timeline?: Date
}

// eslint-disable-next-line @typescript-eslint/ban-types
type AccessorKey<TData> = (string & {}) | keyof TData

export type RecursiveKeyOf<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends Array<unknown>
          ? K
          : T[K] extends object
            ? T[K] extends Date
              ? K
              : K | `${K}.${RecursiveKeyOf<T[K]>}`
            : K
        : never
    }[keyof T & string]
  : never

type AccessorKeyType<TData> = {
  accessorKey: RecursiveKeyOf<TData> | AccessorKey<TData>
}

type ColumnExtra<TData> = {
  header: string
  headerInfo?: React.ReactNode
  columnHeaderClassName?: string
  cellClassName?: string
  hidden?: boolean
  exportable?: boolean
  centerCell?: boolean
  columns?: Column<TData>[]
  render?: (row: TData) => string
  width?: number | string
} & AccessorKeyType<TData>

export type Column<TData> = ColumnDef<TData> & ColumnExtra<TData>

export type Row<TData> = TanstackRow<TData>

export type Table<TData> = TanstackTable<TData>

export interface PaginationResponse<T> {
  data: T[]
  total: number
}

export type NestedObject = { [key: string]: string | NestedObject | boolean }

export type SortingValue = 'asc' | 'desc'
export type PaginationSort<T> = {
  [K in keyof T]?: SortingValue
}
export interface PaginationRequest<
  TFilter = object,
  TAvailableVersions extends string = string,
  TVersion extends TAvailableVersions = TAvailableVersions,
  TSort = NestedObject
> {
  page?: number
  limit?: number
  count?: boolean
  sort?: TSort
  search?: string
  version?: TVersion
  filter?: TFilter
}

export type PaginationInput = {
  pageIndex: number
  pageSize: number
  sorting?: SortingState
}
export type DataTableSelectType<TData> = TData[] | 'ALL'

/**
 * How the header "select all" checkbox behaves:
 * - `in-view` (default): only toggles the rows rendered on the current page into `rows`. Rows
 *   stay individually checkable and selections made on other pages are kept.
 * - `full`: flags every row of the dataset as selected (`isAllSelected`), including the pages
 *   not loaded yet. Rows can no longer be unchecked one by one.
 */
export type DataTableSelectMode = 'full' | 'in-view'

type CsvOptions = {
  fileName: string
  selectedRows?: boolean
}

export type ExpandedColumn<TData, TExpanded extends TableRequiredFields | undefined> = {
  accessorKey: AccessorKey<TData>
  header?: string
  display: (row: TExpanded, parentRow?: TData) => React.ReactNode
}

export type ExpandedDataTableOptions<TData, TExpanded extends TableRequiredFields | undefined> = {
  key: ArrayKeys<TData>
  columns: ExpandedColumn<TData, TExpanded>[]
  alwaysOpen?: boolean
}

export type DataTableSelectedData<
  TData,
  TExpanded extends TableRequiredFields | undefined = undefined
> = {
  isAllSelected: boolean
  rows: TData[]
  expandedRows?: TExpanded[]
}

export type Legend = {
  icon: React.ReactNode
  label: string
}

export type TimelineData<TData> = {
  timeline: Date
  data: TData[]
}

export type FilterOptions<TFilters> = {
  onFiltersClick?: () => void
  selectedFilter?: TFilters
  isTableLoading?: boolean
  setIsTableLoading?: (isLoading: boolean) => void
  discardedFilters?: (keyof TFilters)[]
}

export type DataTableProps<TData, TExpanded extends TableRequiredFields | undefined> = {
  columns: Column<TData>[]
  csvOptions?: CsvOptions
  createText?: string
  /** `'before-select'` renders the order number before the selection checkbox */
  displayOrder?: boolean | 'before-select'
  headerClassName?: string
  tableClassName?: string
  noResults?: string
  expandedOptions?: ExpandedDataTableOptions<TData, TExpanded>
  rowCount: number
  bodyVariant?: 'regular' | 'white'
  disableHover?: boolean
  displayEmptyState?: boolean
  limitControl?: boolean
  display?: boolean
  paginated?: boolean
  state?: Partial<PaginationInput>
  selectable?: boolean
  disableSelectAll?: boolean
  selectMode?: DataTableSelectMode
  isLoading?: boolean
  isSelectAllLoading?: boolean
  footer?: React.ReactNode
  footerCells?: {
    accessorKey: Column<TData>['accessorKey']
    value: React.ReactNode
    colSpan?: number
  }[]
  footerHeader?: React.ReactNode
  footerHeaderClassName?: string
  footerRowClassName?: string
  footerCellClassName?: string
  headerRowClassName?: string
  manualSorting?: boolean
  legends?: Legend[]
  variant?: 'regular' | 'white'
  overflow?: boolean
  selectedData?: DataTableSelectedData<TData, TExpanded>
  hideSelectAllCheckbox?: boolean
  onSelectedDataChange?: (input: DataTableSelectedData<TData, TExpanded>) => void
  createOnClick?: () => void
  onStateChange?: (state: PaginationInput) => void
  onRowClick?: (row: TData, event?: React.MouseEvent) => void
  disableRow?: (row: TData) => boolean
} & (
  | {
      mode?: 'normal'
      data: TData[]
    }
  | {
      mode: 'timeline'
      data: Array<TimelineData<TData>>
    }
  | {
      mode: 'edit'
      data: TData[]
    }
)

type DataTableComponent = <
  TData extends TableRequiredFields,
  TExpanded extends TableRequiredFields | undefined = undefined
>(
  props: DataTableProps<TData, TExpanded> & React.RefAttributes<DataTableRef>
) => React.ReactElement | null

export interface DataTableRef {
  exportToCsv?: () => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DataTable = React.forwardRef<DataTableRef, DataTableProps<any, any>>(function DataTable<
  TData extends TableRequiredFields,
  TExpanded extends TableRequiredFields | undefined
>(
  {
    data,
    headerClassName,
    tableClassName,
    columns,
    displayOrder = false,
    noResults,
    expandedOptions,
    rowCount,
    disableHover,
    isLoading,
    displayEmptyState = true,
    isSelectAllLoading,
    createText,
    limitControl = true,
    display = true,
    variant = 'regular',
    bodyVariant = 'regular',
    paginated = true,
    mode = 'normal',
    selectable,
    disableSelectAll,
    selectMode = 'in-view',
    selectedData,
    state,
    footer,
    footerHeader,
    footerHeaderClassName,
    headerRowClassName,
    footerCells,
    footerRowClassName,
    footerCellClassName,
    manualSorting = true,
    overflow = false,
    csvOptions,
    legends,
    hideSelectAllCheckbox = false,
    createOnClick,
    onSelectedDataChange,
    onStateChange,
    onRowClick,
    disableRow
  }: DataTableProps<TData, TExpanded>,
  ref: React.Ref<DataTableRef>
) {
  const { t } = useTranslate()
  // Determine if component is "controlled" (parent manages state via onStateChange)
  const isControlled = Boolean(onStateChange)

  // Internal state for uncontrolled mode
  const [internalSorting, setInternalSorting] = useState<SortingState>(state?.sorting ?? [])
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: state?.pageIndex ?? 0,
    pageSize: paginated ? (state?.pageSize ?? PAGINATION_LIMIT) : data.length
  })

  // Use controlled state from props when available, otherwise use internal state
  const sorting = isControlled ? (state?.sorting ?? []) : internalSorting
  const pagination = isControlled
    ? {
        pageIndex: state?.pageIndex ?? 0,
        pageSize: paginated ? (state?.pageSize ?? PAGINATION_LIMIT) : data.length
      }
    : internalPagination

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [isGrabbing, setIsGrabbing] = useState(false)
  const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null)

  const formattedData =
    mode === 'normal' || mode === 'edit'
      ? (data as TData[])
      : (data as Array<TimelineData<TData>>).flatMap((obj) => [
          { timeline: obj.timeline } as unknown as TData,
          ...obj.data
        ])

  // The order number goes before the checkbox, outside the table columns, when asked to
  const orderBeforeSelect = displayOrder === 'before-select' && Boolean(selectable)
  const showOrderColumn = displayOrder && !orderBeforeSelect
  // Numbering carries on across pages (page 2 starts at pageSize + 1)
  const orderOffset = paginated ? pagination.pageIndex * pagination.pageSize : 0

  // Create display order column if enabled
  const displayOrderColumn: Column<TData> | null = showOrderColumn
    ? {
        accessorKey: 'id',
        header: t('common.data.table.order'),
        columnHeaderClassName: 'whitespace-nowrap',
        centerCell: true,
        cell: ({ row }) => (
          <Typography variant="secondary-body" className="whitespace-nowrap">
            {orderOffset + row.index + 1}
          </Typography>
        )
      }
    : null

  // Combine display order column with other columns
  const allColumns = [...(displayOrderColumn ? [displayOrderColumn] : []), ...columns].filter(
    (column) => !column.hidden
  )

  const table = useReactTable({
    data: formattedData,
    columns: allColumns,
    rowCount,
    manualPagination: true,
    manualSorting: manualSorting,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      if (isControlled) {
        // Controlled mode: let parent handle state
        onStateChange?.({ pageIndex: 0, pageSize: pagination.pageSize, sorting: next })
      } else {
        // Uncontrolled mode: manage own state
        setInternalSorting(next)
        setInternalPagination((prev) => ({ ...prev, pageIndex: 0 }))
      }
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater
      if (isControlled) {
        // Controlled mode: let parent handle state
        onStateChange?.({
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
          sorting
        })
      } else {
        // Uncontrolled mode: manage own state
        setInternalPagination(next)
      }
    },
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id?.toString() ?? row.timeline?.toString() ?? '',
    state: { sorting, pagination }
  })

  useImperativeHandle(
    ref,
    () => ({
      exportToCsv: () => {
        csvLinkRef.current?.link.click()
      }
    }),
    [csvLinkRef.current]
  )

  const csvData = formattedData
    .filter((d) =>
      csvOptions?.selectedRows
        ? selectedData?.isAllSelected ||
          selectedData?.rows?.find((r) => r.id === d.id) ||
          selectedData?.expandedRows?.find((r) => r?.id === d.id)
        : true
    )
    .map((row) =>
      columns
        .filter((c) => c.exportable !== false)
        .reduce(
          (acc, column) => {
            if (!column.accessorKey) return acc
            acc[column.accessorKey.toString()] =
              column.render?.(row) ?? row[column.accessorKey as keyof TData]
            return acc
          },
          {} as Record<string, unknown>
        )
    )

  const csvHeaders = columns
    .filter((c) => c.exportable !== false)
    .map((column) => {
      if (!column.accessorKey) return null
      return {
        label: column.header?.toString() ?? '',
        key: column.accessorKey.toString()
      }
    })
    .filter((header): header is NonNullable<typeof header> => header !== null)

  const onCheckRow = (row: TData) => {
    if (!selectedData) return
    if (selectedData?.rows?.find((r) => r.id === row.id)) {
      onSelectedDataChange?.({
        ...selectedData,
        rows: selectedData.rows?.filter((r) => r.id !== row.id)
      })
    } else {
      onSelectedDataChange?.({
        ...selectedData,
        rows: [...(selectedData.rows ?? []), row]
      })
    }
  }

  const onCheckExpandedRow = (row: TExpanded) => {
    if (!selectedData) return
    if (selectedData?.expandedRows?.find((r) => r?.id === row?.id))
      onSelectedDataChange?.({
        ...selectedData,
        expandedRows: selectedData.expandedRows?.filter((r) => r?.id !== row?.id)
      })
    else
      onSelectedDataChange?.({
        ...selectedData,
        expandedRows: [...(selectedData.expandedRows ?? []), row]
      })
  }

  // Rows rendered on the current page that the user is allowed to check
  const getSelectableVisibleRows = () =>
    table
      .getRowModel()
      .rows.map((row) => row.original)
      .filter((row) => !disableRow?.(row))

  const getVisibleExpandedRows = () => {
    if (!expandedOptions) return []
    return getSelectableVisibleRows().flatMap(
      (row) => (row[expandedOptions.key] as TExpanded[] | undefined) ?? []
    )
  }

  const areAllVisibleRowsSelected = () => {
    const visibleRows = getSelectableVisibleRows()
    if (!visibleRows.length) return false
    return visibleRows.every((row) => selectedData?.rows?.some((r) => r.id === row.id))
  }

  const isHeaderChecked =
    selectMode === 'in-view' ? areAllVisibleRowsSelected() : !!selectedData?.isAllSelected

  const onCheckAllInView = () => {
    if (!selectedData) return
    const visibleRows = getSelectableVisibleRows()
    const visibleExpandedRows = getVisibleExpandedRows()
    const selectedRows = selectedData.rows ?? []
    const selectedExpandedRows = selectedData.expandedRows ?? []

    if (areAllVisibleRowsSelected()) {
      onSelectedDataChange?.({
        ...selectedData,
        rows: selectedRows.filter((r) => !visibleRows.some((row) => row.id === r.id)),
        expandedRows: selectedExpandedRows.filter(
          (r) => !visibleExpandedRows.some((row) => row?.id === r?.id)
        )
      })
      return
    }

    onSelectedDataChange?.({
      ...selectedData,
      rows: [
        ...selectedRows,
        ...visibleRows.filter((row) => !selectedRows.some((r) => r.id === row.id))
      ],
      expandedRows: [
        ...selectedExpandedRows,
        ...visibleExpandedRows.filter(
          (row) => !selectedExpandedRows.some((r) => r?.id === row?.id)
        )
      ]
    })
  }

  const onCheckAll = () => {
    if (isSelectAllLoading) return // Prevent clicking when loading
    if (selectMode === 'in-view') return onCheckAllInView()
    if (selectedData?.isAllSelected)
      onSelectedDataChange?.({ isAllSelected: false, rows: [], expandedRows: [] })
    else onSelectedDataChange?.({ isAllSelected: true, rows: [], expandedRows: [] })
  }

  // Helper function to get the correct column configuration for nested columns
  const getColumnConfigForCell = (cellIndex: number) => {
    let currentIndex = 0
    for (const column of allColumns) {
      if (column.columns && column.columns.length > 0) {
        // This is a nested column
        for (let i = 0; i < column.columns.length; i++) {
          if (currentIndex === cellIndex) {
            return {
              parentColumn: column,
              nestedColumn: column.columns[i],
              isNested: true,
              nestedIndex: i
            }
          }
          currentIndex++
        }
      } else {
        // This is a regular column
        if (currentIndex === cellIndex) {
          return {
            parentColumn: column,
            nestedColumn: null,
            isNested: false,
            nestedIndex: 0
          }
        }
        currentIndex++
      }
    }
    return null
  }

  const cellStyle = (row: TableRequiredFields): React.CSSProperties => ({
    backgroundColor: row.rowColor,
    ...(row.rowBorderColor
      ? {
          borderBottom: `2px solid ${row.rowBorderColor}`,
          borderTop: `2px solid ${row.rowBorderColor}`
        }
      : {})
  })

  if (!display) return null

  return (
    <div>
      <CSVLink
        ref={csvLinkRef}
        data={csvData}
        filename={(csvOptions?.fileName ?? 'export') + '.csv'}
        headers={csvHeaders}
      />
      <Table
        overflow={overflow}
        onGrabAction={setIsGrabbing}
        className={cn({ 'bg-white': bodyVariant === 'white' })}
        wrapperClassName={tableClassName}
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow className={headerRowClassName} key={headerGroup.id}>
              {expandedOptions && (
                <TableHead
                  className={cn(
                    'pl-0',
                    { 'rounded-tl-3xl': headerGroup.depth === 0 },
                    headerClassName
                  )}
                />
              )}
              {orderBeforeSelect && (
                <TableHead
                  className={cn(
                    // Stays visible while the table scrolls sideways
                    'font-avenir-book text-xs !font-bold sticky left-0 z-[1]',
                    {
                      'bg-white': variant === 'white',
                      'rounded-tl-3xl': !expandedOptions && headerGroup.depth === 0
                    },
                    headerClassName
                  )}
                >
                  {t('common.data.table.order')}
                </TableHead>
              )}
              {selectable && (
                <TableHead
                  className={cn(
                    {
                      'pl-0': expandedOptions,
                      'pl-4': !expandedOptions,
                      'rounded-tl-3xl':
                        !expandedOptions && !orderBeforeSelect && headerGroup.depth === 0,
                      '!cursor-default': disableSelectAll || isSelectAllLoading
                    },
                    headerClassName
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!hideSelectAllCheckbox && !disableSelectAll) onCheckAll()
                  }}
                  style={{ cursor: isSelectAllLoading ? 'default' : 'pointer' }} // Show pointer only when clickable
                >
                  {isSelectAllLoading ? (
                    <Loader />
                  ) : (
                    !hideSelectAllCheckbox &&
                    !disableSelectAll && (
                      <Checkbox checked={isHeaderChecked} onChange={() => onCheckAll()} />
                    )
                  )}
                </TableHead>
              )}
              {headerGroup.headers.map((header, index) => {
                const sort = header.column.getIsSorted()
                const columnConfig = getColumnConfigForCell(index)
                // Only apply the parent columnHeaderClassName to the first nested column
                let columnHeaderClassName = ''
                if (columnConfig?.isNested) {
                  columnHeaderClassName =
                    columnConfig.nestedIndex === 0
                      ? columnConfig.parentColumn.columnHeaderClassName || ''
                      : ''
                } else {
                  columnHeaderClassName = columnConfig?.parentColumn.columnHeaderClassName || ''
                }
                const centerCell = columnConfig?.parentColumn.centerCell
                const width = columnConfig?.parentColumn.width

                const columnDef = header.column.columnDef as Column<TData>

                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'font-avenir-book text-xs !font-bold',
                      {
                        'bg-white': variant === 'white',
                        // Outer corners only: the first header row (nested header rows sit inside)
                        'rounded-tr-3xl':
                          headerGroup.depth === 0 && headerGroup.headers.length - 1 === index,
                        'rounded-tl-3xl':
                          headerGroup.depth === 0 &&
                          !expandedOptions &&
                          index === 0 &&
                          !selectable &&
                          !expandedOptions,
                        // Only apply borders to main columns that have subcolumns
                        'border-b border-grey-light': header.colSpan > 1,
                        'border-r border-grey-light':
                          header.colSpan > 1 && index !== headerGroup.headers.length - 1
                      },
                      headerClassName,
                      columnHeaderClassName
                    )}
                    colSpan={header.colSpan}
                    style={width ? { width, minWidth: width, maxWidth: width } : undefined}
                  >
                    {columnDef.enableSorting ? (
                      <button
                        type="button"
                        onClick={() => {
                          const isSorted = header.column.getIsSorted()
                          const sortDescFirst = header.column.columnDef.sortDescFirst

                          // If it's already sorted, toggle the sorting
                          if (isSorted === 'desc') {
                            if (sortDescFirst) header.column.toggleSorting(false)
                            else header.column.clearSorting()
                          } else if (isSorted === 'asc') {
                            if (sortDescFirst) header.column.clearSorting()
                            else header.column.toggleSorting(true)
                          }
                          // Initially sort based on `sortDescFirst` condition
                          else header.column.toggleSorting(sortDescFirst ?? false)
                        }}
                        className="flex items-center bg-transparent border-none focus:outline-none cursor-pointer hover:text-primary-hover [&>svg]:hover:fill-primary transition-colors duration-75"
                      >
                        {header.isPlaceholder ? null : centerCell ? (
                          <div className="flex justify-center items-center w-full gap-2">
                            {flexRender(columnDef.header, header.getContext())}
                            {columnDef.headerInfo && columnDef.headerInfo}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {flexRender(columnDef.header, header.getContext())}
                            {columnDef.headerInfo && columnDef.headerInfo}
                          </div>
                        )}
                        {!sort ? (
                          <ArrowUpDownIcon className={'fill-black min-w-6'} />
                        ) : sort === 'asc' ? (
                          header.column.columnDef.invertSorting ? (
                            <ArrowDownIcon className={'fill-black min-w-6'} />
                          ) : (
                            <ArrowUpIcon className={'fill-black min-w-6'} />
                          )
                        ) : sort === 'desc' ? (
                          header.column.columnDef.invertSorting ? (
                            <ArrowUpIcon className={'fill-black min-w-6'} />
                          ) : (
                            <ArrowDownIcon className={'fill-black min-w-6'} />
                          )
                        ) : null}
                      </button>
                    ) : header.isPlaceholder ? null : centerCell ? (
                      <div className="flex justify-center items-center w-full gap-2">
                        {flexRender(columnDef.header, header.getContext())}
                        {columnDef.headerInfo && columnDef.headerInfo}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {flexRender(columnDef.header, header.getContext())}
                        {columnDef.headerInfo && columnDef.headerInfo}
                      </div>
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              return (
                <Fragment key={row.id}>
                  {row.original.timeline && mode === 'timeline' ? (
                    <TableRow className="h-8 border-none relative">
                      <TableCell
                        colSpan={columns.length}
                        className="absolute h-full top-0 left-0 w-full bg-secondary-light flex items-center justify-between px-4"
                      >
                        {Array.from({ length: 3 }).map((_, i) => (
                          <span key={i} className="text-secondary font-semibold select-none">
                            {row.original.timeline
                              ? DateFormatter.DDMMYYYY(row.original.timeline)
                              : '-'}
                          </span>
                        ))}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className={cn('h-14 text-xs font-medium whitespace-nowrap', {
                        '[&>td]:hover:bg-primary-light': !disableHover && !isGrabbing,
                        'cursor-pointer': Boolean(onRowClick) && !isGrabbing,
                        'bottom-shadow': expandedRows.has(row.id),
                        '[&>td]:bg-primary-light':
                          selectedData?.isAllSelected ||
                          selectedData?.rows?.find((r) => r.id === row.original.id)
                      })}
                      onClick={(e) => {
                        if (!isGrabbing) onRowClick?.(row.original, e)
                      }}
                    >
                      {expandedOptions && (
                        <TableCell className="duration-200" style={cellStyle(row.original)}>
                          {!!(row.original[expandedOptions.key] as TExpanded[])?.length &&
                            !expandedOptions?.alwaysOpen && (
                              <SolidUpArrowIcon
                                className={cn('fill-black duration-200', {
                                  'transform rotate-180': !expandedRows.has(row.id)
                                })}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (expandedRows.has(row.id))
                                    setExpandedRows((prev) => {
                                      prev.delete(row.id)
                                      return new Set(prev)
                                    })
                                  else setExpandedRows((prev) => new Set(prev).add(row.id))
                                }}
                              />
                            )}
                        </TableCell>
                      )}

                      {orderBeforeSelect && (
                        <TableCell
                          // Opaque so the columns scrolling under it stay hidden
                          className="duration-200 sticky left-0 z-[1] bg-white"
                          style={cellStyle(row.original)}
                        >
                          <Typography variant="secondary-body" className="whitespace-nowrap">
                            {orderOffset + row.index + 1}
                          </Typography>
                        </TableCell>
                      )}
                      {selectable && (
                        <TableCell
                          className={cn('duration-200', {
                            'pl-0': expandedOptions,
                            'pl-4': !expandedOptions
                          })}
                          style={cellStyle(row.original)}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (disableRow?.(row.original)) return
                            onCheckRow(row.original)
                          }}
                        >
                          <Checkbox
                            checked={
                              disableRow?.(row.original)
                                ? false
                                : selectedData?.isAllSelected ||
                                  !!selectedData?.rows?.find((r) => r?.id === row.original.id)
                            }
                            disabled={disableRow?.(row.original) || selectedData?.isAllSelected}
                            onChange={() => {
                              onCheckRow(row.original)
                            }}
                          />
                        </TableCell>
                      )}

                      {row.getVisibleCells().map((cell, index) => {
                        const columnConfig = getColumnConfigForCell(index)
                        let cellClassName = ''
                        if (columnConfig?.isNested) {
                          // Only apply border to the first nested column
                          cellClassName =
                            columnConfig.nestedIndex === 0
                              ? columnConfig.parentColumn.cellClassName || ''
                              : ''
                        } else {
                          cellClassName = columnConfig?.parentColumn.cellClassName || ''
                        }
                        const centerCell = columnConfig?.isNested
                          ? columnConfig.parentColumn.centerCell
                          : columnConfig?.parentColumn.centerCell
                        const width = columnConfig?.parentColumn.width

                        return (
                          <TableCell
                            key={cell.id}
                            className={cn('duration-200 h-[inherit]', cellClassName)}
                            style={{
                              ...cellStyle(row.original),
                              ...(width ? { width, minWidth: width, maxWidth: width } : {})
                            }}
                          >
                            {centerCell ? (
                              <div className="flex justify-center items-center w-full">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )}

                  {expandedOptions &&
                    (expandedRows.has(row.id) || expandedOptions?.alwaysOpen) &&
                    (() => {
                      const expandedData = row.original[expandedOptions?.key] as TExpanded[]
                      const hasHeaders = expandedOptions.columns.some((col) => col.header)

                      return (
                        <>
                          {/* Header row for expanded columns if any headers are defined */}
                          {hasHeaders && expandedData?.length > 0 && (
                            <TableRow
                              key={`${row.id}-header`}
                              className="h-12 text-xs font-medium whitespace-nowrap bg-white-active"
                            >
                              <TableCell
                                className="bg-white-active"
                                style={cellStyle(row.original)}
                              />
                              {orderBeforeSelect && (
                                <TableCell
                                  className="bg-gray-50"
                                  style={cellStyle(row.original)}
                                />
                              )}
                              {selectable && (
                                <TableCell
                                  className="bg-gray-50"
                                  style={cellStyle(row.original)}
                                />
                              )}
                              {columns.map((column) => {
                                const expandedColumn = expandedOptions.columns.find(
                                  (c) => c.accessorKey === column.accessorKey
                                )

                                return (
                                  <TableCell
                                    className="bg-gray-50 font-semibold bg-white-active"
                                    key={`${column.accessorKey?.toString()}-header`}
                                    style={cellStyle(row.original)}
                                  >
                                    <Typography variant={'caption'}>
                                      {expandedColumn?.header ?? ''}
                                    </Typography>
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          )}

                          {/* Data rows for expanded content */}
                          {expandedData?.map((expandedRow) => (
                            <TableRow
                              key={expandedRow?.id}
                              className={cn('h-14 text-xs font-light whitespace-nowrap', {
                                '[&>td]:hover:bg-primary-light': !disableHover && !isGrabbing,
                                'cursor-pointer': Boolean(onRowClick) && !isGrabbing,
                                '[&>td]:bg-primary-light':
                                  selectedData?.isAllSelected ||
                                  selectedData?.expandedRows?.find(
                                    (r) => r?.id === expandedRow?.id
                                  )
                              })}
                              style={cellStyle(row.original)}
                              onClick={(e) => {
                                if (!isGrabbing) onRowClick?.(row.original, e)
                              }}
                            >
                              <TableCell
                                className=" bg-white-active"
                                style={cellStyle(row.original)}
                              />
                              {orderBeforeSelect && (
                                <TableCell
                                  className=" bg-white-active"
                                  style={cellStyle(row.original)}
                                />
                              )}
                              {selectable && (
                                <TableCell
                                  className=" bg-white-active"
                                  style={cellStyle(row.original)}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onCheckExpandedRow(expandedRow)
                                  }}
                                >
                                  <Checkbox
                                    checked={
                                      selectedData?.isAllSelected ||
                                      !!selectedData?.expandedRows?.find(
                                        (r) => r?.id === expandedRow?.id
                                      )
                                    }
                                    disabled={selectedData?.isAllSelected}
                                    onChange={() => {
                                      onCheckExpandedRow(expandedRow)
                                    }}
                                  />
                                </TableCell>
                              )}
                              {columns.map((column) => {
                                const key = expandedOptions?.key
                                const expandedColumns = expandedOptions?.columns
                                if (!key) return null

                                return (
                                  <TableCell
                                    className=" bg-white-active"
                                    key={column.accessorKey?.toString()}
                                    style={cellStyle(row.original)}
                                  >
                                    {expandedColumns
                                      ?.find((c) => c.accessorKey === column.accessorKey)
                                      ?.display(expandedRow, row.original) ?? ''}
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          ))}
                        </>
                      )
                    })()}
                </Fragment>
              )
            })
          ) : !createText && displayEmptyState ? (
            <TableRow className={cn('relative')}>
              <TableCell
                colSpan={
                  expandedOptions
                    ? columns.length + 1
                    : columns.reduce((acc, col) => acc + (col.columns?.length ?? 1), 0)
                }
                className={
                  'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center mobile:static mobile:transform-none '
                }
              >
                {isLoading ? (
                  <div className="flex justify-center items-center h-full">
                    <Loader />
                  </div>
                ) : (
                  (noResults ?? t('common.no.results'))
                )}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
        {createText && (
          <TableRow>
            <TableCell
              colSpan={
                expandedOptions
                  ? columns.length + 1
                  : columns.reduce((acc, col) => acc + (col.columns?.length ?? 1), 0)
              }
              className={'h-14 text-center bg-primary-light cursor-pointer'}
              onClick={createOnClick}
            >
              <div className="flex gap-2 items-center justify-center">
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-primary">
                  <PlusIcon width="14px" height="14px" color={tailwindColors.white.DEFAULT} />
                </div>
                <Typography variant="label-medium" className="text-primary">
                  {createText}
                </Typography>
              </div>
            </TableCell>
          </TableRow>
        )}

        {footerHeader && (
          <TableRow className={footerHeaderClassName}>
            <TableCell
              colSpan={
                expandedOptions
                  ? columns.length + 1
                  : columns.reduce((acc, col) => acc + (col.columns?.length ?? 1), 0)
              }
              className={footerCellClassName}
            >
              {footerHeader}
            </TableCell>
          </TableRow>
        )}

        {footerCells && (
          <TableFooter>
            {table.getHeaderGroups().map((headerGroup) => {
              let skipCount = 0
              return (
                <TableRow
                  key={headerGroup.id}
                  className={cn(
                    'border-none',
                    { 'bg-white': bodyVariant === 'white' },
                    footerRowClassName
                  )}
                >
                  {expandedOptions && <TableCell className={footerCellClassName} />}
                  {orderBeforeSelect && <TableCell className={footerCellClassName} />}
                  {selectable && <TableCell className={footerCellClassName} />}
                  {headerGroup.headers.map((header) => {
                    // Skip cells that are covered by a previous colSpan
                    if (skipCount > 0) {
                      skipCount--
                      return null
                    }

                    const columnDef = header.column.columnDef as Column<TData>
                    const accessorKey = columnDef.accessorKey
                    const footerCell = footerCells.find(
                      (cell) => accessorKey?.toString() === cell.accessorKey?.toString()
                    )

                    // If this cell has a colSpan > 1, skip the next (colSpan - 1) cells
                    const colSpan = footerCell?.colSpan ?? header.colSpan
                    if (colSpan > 1) {
                      skipCount = colSpan - 1
                    }

                    return (
                      <TableCell key={header.id} colSpan={colSpan} className={footerCellClassName}>
                        {footerCell?.value ?? <div />}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableFooter>
        )}

        {footer && (
          <TableFooter>
            <TableRow
              className={cn(
                'border-none',
                { 'bg-white': bodyVariant === 'white' },
                footerRowClassName
              )}
            >
              <TableCell
                colSpan={
                  expandedOptions
                    ? columns.length + 1 + (selectable ? 1 : 0) + (orderBeforeSelect ? 1 : 0)
                    : columns.reduce((acc, col) => acc + (col.columns?.length ?? 1), 0) +
                      (selectable ? 1 : 0) +
                      (orderBeforeSelect ? 1 : 0)
                }
                className={footerCellClassName}
              >
                {footer}
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>

      {legends && (
        <div className="flex gap-5 items-center mt-4">
          {legends.map((l, index) => (
            <div key={index} className="flex items-center gap-1">
              {l.icon}
              <Typography variant="label-semibold">{l.label}</Typography>
            </div>
          ))}
        </div>
      )}

      {paginated && (
        <Pagination
          page={pagination.pageIndex}
          limit={pagination.pageSize}
          total={rowCount}
          limitControl={limitControl}
          pageCount={table.getPageCount()}
          onPageChange={({ selected }) => table.setPageIndex(selected)}
          onLimitChange={(limit) => table.setPageSize(limit)}
        />
      )}
    </div>
  )
}) as DataTableComponent

export default DataTable
